import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { showSwal } from "../../Helper/ShowAlert";
import {
  Api_URI,
  getNotifications,
  markNotificationRead as markNotificationReadApi,
  markAllNotificationsRead as markAllNotificationsReadApi,
} from "../../Api";
import { useSideBarContext } from "../../Providers/SideBarProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisVertical,
  faBell,
  faCircle,
  faExclamationTriangle,
  faInfo,
  faUser,
  faClock,
  faBuilding,
  faGear,
  faRightFromBracket,
  faChevronDown,
  faVolumeHigh,
  faVolumeMute,
  faQuestionCircle,
  faCheckCircle,
  faCircleNotch,
  faBolt,
} from "@fortawesome/free-solid-svg-icons";
import "./Admin.css";
import StageChecklist from "../Quotes/components/StageChecklist";
import {
  logout,
  addRealTimeNotification,
  seedNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  bumpQuoteListRefresh,
} from "../../redux/action";
import {
  playNotificationSound,
  isNotificationSoundMuted,
  setNotificationSoundMuted,
} from "../../Helper/soundUtils";
import { addLiveMessage } from "../../redux/threadSlice";
import BreadcrumbNav from "../../components/BreadcrumbNav";
import { notificationTarget } from "./notificationTarget";
import { useDarkMode } from "../../context/DarkModeContext";
import { handleRole } from "../../Helper/handleRoleHelper";
import Tooltip from "../../components/shared/Tooltip";
import { popIn } from "../../components/motion/presets";
import { PureOpens } from "../Quotes/QuoteAllDetails";
import { useWalkthrough, tourIdForPathname } from "../../walkthrough";
import { toast } from "react-toastify";
import AnimatedLoader from "../../motionKit/AnimatedLoader";

/** Visual recipe for the ephemeral “live SSE status” chip above the Live pill */
const SSE_TRANSIENT_TOAST_TONE = {
  open: {
    icon: faCheckCircle,
    iconSpin: false,
    iconWrap:
      "bg-gradient-to-br from-emerald-400/25 to-teal-500/15 text-emerald-700 shadow-inner shadow-emerald-500/10 dark:from-emerald-400/20 dark:to-teal-600/10 dark:text-emerald-300",
    panel:
      "border-emerald-200/95 bg-white/95 shadow-[0_16px_50px_-14px_rgba(16,185,129,0.48),inset_0_1px_0_rgba(255,255,255,0.85)] ring-1 ring-emerald-400/20 backdrop-blur-xl dark:border-emerald-500/40 dark:bg-[oklch(0.168_0.021_264/0.96)] dark:shadow-[0_16px_50px_-14px_rgba(16,185,129,0.22)] dark:ring-emerald-500/15",
    sheen: "from-emerald-400/20 via-white/40 to-cyan-400/15 opacity-90 dark:from-emerald-500/15 dark:via-transparent dark:to-teal-500/10 dark:opacity-100",
    arrow:
      "border-emerald-300/85 bg-gradient-to-br from-white via-emerald-50/90 to-teal-50/70 shadow-[0_2px_8px_-2px_rgba(16,185,129,0.35)] dark:border-emerald-500/45 dark:from-slate-950 dark:via-emerald-950/50 dark:to-slate-950",
  },
  connecting: {
    icon: faCircleNotch,
    iconSpin: true,
    iconWrap:
      "bg-gradient-to-br from-amber-400/30 to-orange-500/15 text-amber-800 shadow-inner dark:from-amber-400/22 dark:to-amber-600/12 dark:text-amber-200",
    panel:
      "border-amber-200/95 bg-white/95 shadow-[0_16px_50px_-14px_rgba(245,158,11,0.42),inset_0_1px_0_rgba(255,255,255,0.85)] ring-1 ring-amber-400/25 backdrop-blur-xl dark:border-amber-500/40 dark:bg-[oklch(0.168_0.021_264/0.96)] dark:shadow-[0_16px_50px_-14px_rgba(245,158,11,0.2)] dark:ring-amber-500/15",
    sheen: "from-amber-400/18 via-white/30 to-orange-400/12 opacity-90 dark:from-amber-500/12 dark:via-transparent dark:to-orange-500/10 dark:opacity-100",
    arrow:
      "border-amber-300/85 bg-gradient-to-br from-white via-amber-50/95 to-orange-50/70 shadow-[0_2px_8px_-2px_rgba(245,158,11,0.35)] dark:border-amber-500/45 dark:from-slate-950 dark:via-amber-950/45 dark:to-slate-950",
  },
  offline: {
    icon: faBolt,
    iconSpin: false,
    iconWrap:
      "bg-gradient-to-br from-rose-400/28 to-orange-500/15 text-rose-800 shadow-inner dark:from-rose-400/22 dark:to-rose-600/12 dark:text-rose-200",
    panel:
      "border-rose-200/95 bg-white/95 shadow-[0_16px_50px_-14px_rgba(244,63,94,0.38),inset_0_1px_0_rgba(255,255,255,0.85)] ring-1 ring-rose-400/22 backdrop-blur-xl dark:border-rose-500/40 dark:bg-[oklch(0.168_0.021_264/0.96)] dark:shadow-[0_16px_50px_-14px_rgba(244,63,94,0.18)] dark:ring-rose-500/15",
    sheen: "from-rose-400/18 via-white/28 to-fuchsia-400/12 opacity-90 dark:from-rose-500/12 dark:via-transparent dark:to-fuchsia-500/10 dark:opacity-100",
    arrow:
      "border-rose-300/85 bg-gradient-to-br from-white via-rose-50/95 to-orange-50/60 shadow-[0_2px_8px_-2px_rgba(244,63,94,0.32)] dark:border-rose-500/45 dark:from-slate-950 dark:via-rose-950/45 dark:to-slate-950",
  },
};

function SseTransientToastBubble({ sseStatus, message }) {
  const tone =
    SSE_TRANSIENT_TOAST_TONE[sseStatus] || SSE_TRANSIENT_TOAST_TONE.open;
  return (
    <>
      <div className={`relative overflow-hidden rounded-2xl border ${tone.panel}`}>
        <div
          className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${tone.sheen}`}
          aria-hidden
        />
        <div className="pointer-events-none absolute inset-x-3 top-0 h-px bg-gradient-to-r from-transparent via-white/70 to-transparent opacity-80 dark:via-white/10" />
        <div className="relative flex items-start gap-3 px-3.5 pb-3 pt-3">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tone.iconWrap}`}
          >
            <FontAwesomeIcon
              icon={tone.icon}
              className={`text-[17px] ${tone.iconSpin ? "animate-spin" : ""}`}
            />
          </span>
          <p className="min-w-0 flex-1 pt-1 text-left text-[13px] font-semibold leading-snug tracking-tight text-slate-800 dark:text-slate-50">
            {message}
          </p>
        </div>
      </div>
      <div className="pointer-events-none absolute -bottom-[7px] right-10 z-[1] flex h-4 w-4 items-start justify-center overflow-visible">
        <div
          className={`h-2.5 w-2.5 rotate-45 rounded-[3px] border ${tone.arrow}`}
          aria-hidden
        />
      </div>
    </>
  );
}

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const loggedInUser = useSelector((state) => state?.reducer?.userLoggedIn);
  const loginToken = useSelector((state) => state?.reducer?.userToken);
  const { realTimeNotifications, unreadCount } = useSelector((state) => state?.notifications);
  const { isOpen, toggleSidebar, activePage } = useSideBarContext();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { startTour } = useWalkthrough();

  const pageTourId = useMemo(() => tourIdForPathname(location.pathname), [location.pathname]);

  const handleStartPageTour = useCallback(() => {
    startTour(pageTourId, { force: true });
  }, [startTour, pageTourId]);

  const [mySideBarOpen, setMySideBarOpen] = useState(!isOpen);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const notificationPanelRef = useRef(null);
  const notificationButtonRef = useRef(null);
  const profileMenuRef = useRef(null);
  const profileButtonRef = useRef(null);
  const sseConnectingRef = useRef(false); // Prevent duplicate SSE connections
  const reconnectAttemptsRef = useRef(0);
  const sseStatusRef = useRef("connecting"); // "connecting" | "open" | "offline"
  const forceReconnectRef = useRef(null);
  const connectInFlightRef = useRef(null);
  const connectionEpochRef = useRef(0);
  const [sseStatus, setSseStatus] = useState("connecting");
  const [transientStatusMsg, setTransientStatusMsg] = useState(null);
  const lastManualReconnectRef = useRef(0);
  const prevSseStatusRef = useRef(null);
  const [notificationPosition, setNotificationPosition] = useState({ top: 0, right: 0 });
  const [soundMuted, setSoundMuted] = useState(() => isNotificationSoundMuted());

  const userInitial = (loggedInUser?.name || loggedInUser?.email || "U").trim().slice(0, 1).toUpperCase();

  const entityConfig = {
    customer: {
      icon: faUser,
      tile: "bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300",
      pill: "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
    },
    airline: {
      icon: faInfo,
      tile: "bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300",
      pill: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
    },
    shipper: {
      icon: faInfo,
      tile: "bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300",
      pill: "bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300",
    },
    agent: {
      icon: faInfo,
      tile: "bg-rose-50 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300",
      pill: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
    },
    transporter: {
      icon: faInfo,
      tile: "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300",
      pill: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
    },
    system: {
      icon: faExclamationTriangle,
      tile: "bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-300",
      pill: "bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300",
    },
  };

  const capitalizeFirst = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : "");

  const getConversationConfig = (entityType) => {
    const entityTypeToTab = {
      customer: "CustomerConversation",
      airline: "AirlineConversation",
      shipper: "ShipperConversation",
      agent: "TransportConversation",
      transporter: "TransportConversation",
    };
    const conversationTab = entityTypeToTab[entityType] || "CustomerConversation";
    return {
      conversationTab,
      defaultOpen: {
        ...PureOpens,
        [conversationTab]: true,
      },
    };
  };

  const flashStatusToast = useCallback((msg) => {
    setTransientStatusMsg(msg);
    setTimeout(() => setTransientStatusMsg(null), 1800);
  }, []);

  const handleStatusClick = useCallback(() => {
    const es = window.__eventSource;
    const live = es && es.readyState === EventSource.OPEN;

    if (sseStatus === "open" && live) {
      flashStatusToast("Live updates already active.");
      return;
    }
    if (sseStatus === "connecting") {
      flashStatusToast("Reconnecting — please wait…");
      return;
    }
    const now = Date.now();
    if (now - lastManualReconnectRef.current < 2000) {
      flashStatusToast("Already retrying…");
      return;
    }
    lastManualReconnectRef.current = now;
    if (forceReconnectRef.current) {
      forceReconnectRef.current("manual_click");
      flashStatusToast("Reconnecting…");
    }
  }, [sseStatus, flashStatusToast]);

  const normalizeNotification = (n) => ({
    id: n._id || Date.now() + Math.random(),
    quoteId: n.quoteId || "",
    threadId: n.threadId || "",
    entityType: n.entityType || "customer",
    entityName: n.entityName || "Unknown",
    entityType_: n.entityType || "",
    type: n.type || "",
    sourceEmail: n.sourceEmail || "",
    lastAction: n.lastAction || "Update received",
    displayText:
      n.message ||
      `${capitalizeFirst(n.entityType || "customer")} ${n.entityName || "Unknown"} notification received`,
    timestamp: new Date(n.createdAt || Date.now()).toLocaleString(),
    read: false,
  });

  useEffect(() => {
    setTimeout(() => {
      setMySideBarOpen(isOpen);
    }, 150);
  }, [isOpen]);

  // Seed the bell with recent history.
  //
  // The SSE stream deliberately pushes NO backlog on connect — see
  // config/sse/sseHandlers.js:35, "Pending notifications are NOT pushed over
  // SSE on connect. The frontend already fetches them via GET
  // /api/<role>/notifications." That second half was never true of this header,
  // so the dropdown sat empty on every page load until a live event happened to
  // arrive. (replayRecentSseEvents only replays for a RECONNECT carrying a
  // Last-Event-ID; a first connect gets nothing — sseManager.js:52.)
  useEffect(() => {
    if (!loginToken || !loggedInUser?.role) return;
    let cancelled = false;

    (async () => {
      try {
        const response = await getNotifications(
          loginToken,
          loggedInUser.role,
          1,
          50
        );
        if (cancelled || !Array.isArray(response?.data)) return;
        dispatch(
          seedNotifications(
            response.data.map((n) => ({
              ...normalizeNotification(n),
              // normalizeNotification hardcodes read:false for the live path;
              // history must keep whatever the server says. The schema path is
              // `isRead` — reading `read` here counted every stored
              // notification as unread, which is why the badge never cleared.
              read: Boolean(n.isRead ?? n.read),
            }))
          )
        );
      } catch (err) {
        // A failed history fetch must not break the live bell — SSE still works.
        console.error("Failed to load notification history:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loginToken, loggedInUser?.role, dispatch]);

  const { id } = useParams();
  useEffect(() => {
    if (!loginToken) return;

    let reconnectTimeout = null;
    const MAX_RECONNECT_ATTEMPTS = 10;
    const INITIAL_RECONNECT_DELAY = 1000; // Start with 1 second
    const MAX_RECONNECT_DELAY = 30000; // Max 30 seconds

    const connectSSE = () => {
      if (connectInFlightRef.current) {
        return connectInFlightRef.current;
      }

      const myEpoch = ++connectionEpochRef.current;

      const p = Promise.resolve()
        .then(() => {
          if (window.__eventSource) {
            try {
              const old = window.__eventSource;
              old.onopen = null;
              old.onmessage = null;
              old.onerror = null;
              old.close();
            } catch (err) {
              console.warn("SSE: error closing previous ES:", err);
            }
            window.__eventSource = null;
          }

          if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
            console.error(`❌ SSE: Max reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) reached.`);
            sseConnectingRef.current = false;
            sseStatusRef.current = "offline";
            setSseStatus("offline");
            return;
          }

          sseConnectingRef.current = true;
          sseStatusRef.current = "connecting";
          setSseStatus("connecting");

          const es = new EventSource(
            `${Api_URI}/api/${handleRole(loggedInUser?.role)}/sse/setup?token=${loginToken}`
          );
          window.__eventSource = es;

          window.__sseCount = (window.__sseCount || 0) + 1;
          if (window.__sseCount > 1) {
            console.warn(`⚠️ SSE: more than one active connection: ${window.__sseCount}`);
          }

          es.onopen = () => {
            if (myEpoch !== connectionEpochRef.current) {
              try {
                es.onopen = null;
                es.onmessage = null;
                es.onerror = null;
                es.close();
              } catch {
                /* ignore */
              }
              window.__sseCount = Math.max(0, (window.__sseCount || 1) - 1);
              return;
            }
            console.log("✅ SSE connected");
            reconnectAttemptsRef.current = 0;
            sseConnectingRef.current = false;
            sseStatusRef.current = "open";
            setSseStatus("open");
          };

          es.onmessage = (event) => {
            if (myEpoch !== connectionEpochRef.current) {
              return;
            }
            try {
              // Ignore heartbeat/keepalive messages
              if (event.data.startsWith(":") || event.data.trim() === "") {
                return;
              }

              const parsed = JSON.parse(event.data);
              if (parsed.type === "thread_update") {
                // Trust the backend-rendered message (fetchDomainUsers.handleMappedThread
                // formats it per lastAction). Falling back to a locally-rebuilt string here
                // overrode the new "X replied" wording and made the change look like a no-op.
                const entityLabel = capitalizeFirst(parsed.entityType || "customer");
                const fallbackMsg =
                  parsed.lastAction === "started_conversation"
                    ? `${entityLabel} ${parsed.entityName || "Unknown"} has sent a query`
                    : `${entityLabel} ${parsed.entityName || "Unknown"} replied`;
                const toastMsg = parsed.message || fallbackMsg;

                const newNotification = normalizeNotification({
                  _id: parsed._id,
                  quoteId: parsed.quoteId,
                  threadId: parsed.threadId,
                  entityType: parsed.entityType,
                  entityName: parsed.entityName,
                  lastAction: parsed.lastAction,
                  message: toastMsg,
                  createdAt: new Date(),
                });

                dispatch(addRealTimeNotification(newNotification));

                if (parsed.parsedMessage?.liveMessage) {
                  dispatch(
                    addLiveMessage({
                      threadId: parsed.threadId,
                      quoteId: parsed.quoteId || null,
                      message: parsed.parsedMessage.liveMessage,
                    })
                  );
                }
                // Bump the quotes list when a row's display state changes:
                // - any started_conversation (customer + first-touch entities)
                // - airline/agent rate replies so 'waiting' flips to 'rate received'
                const isCustomerStart =
                  parsed.lastAction === "started_conversation" &&
                  (parsed.entityType === "customer" || !parsed.entityType);
                const isRateReceived =
                  (parsed.entityType === "airline" ||
                    parsed.entityType === "agent") &&
                  (parsed.lastAction === "sent_rate" ||
                    parsed.lastAction === "replied_with_rates");
                if (isCustomerStart || isRateReceived) {
                  dispatch(
                    bumpQuoteListRefresh({
                      lastAction: parsed.lastAction,
                      quoteId: parsed.quoteId,
                      quoteRow: parsed.quoteRow || null,
                    })
                  );
                }
                playNotificationSound();
              }
            } catch (err) {
              console.error("❌ Failed to parse SSE message", err);
            }
          };

          es.onerror = (err) => {
            if (myEpoch !== connectionEpochRef.current) {
              return;
            }
            const state = es.readyState;
            console.warn(`⚠️ SSE connection error (state: ${state})`, err);

            try {
              es.onopen = null;
              es.onmessage = null;
              es.onerror = null;
              es.close();
            } catch {
              /* ignore */
            }
            if (window.__eventSource === es) {
              window.__eventSource = null;
            }
            window.__sseCount = Math.max(0, (window.__sseCount || 1) - 1);
            sseConnectingRef.current = false;
            sseStatusRef.current = "connecting";
            setSseStatus("connecting");

            if (state === EventSource.CLOSED) {
              reconnectAttemptsRef.current++;
              if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
                console.error(`❌ SSE: Max reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) reached. Please refresh the page.`);
                sseConnectingRef.current = false;
                sseStatusRef.current = "offline";
                setSseStatus("offline");
                return;
              }
              const delay = Math.min(
                INITIAL_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current - 1),
                MAX_RECONNECT_DELAY
              );
              console.log(
                `🔄 SSE: Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})...`
              );
              if (reconnectTimeout) clearTimeout(reconnectTimeout);
              reconnectTimeout = setTimeout(() => {
                if (myEpoch === connectionEpochRef.current) {
                  connectSSE();
                }
              }, delay);
            }
          };
        })
        .finally(() => {
          connectInFlightRef.current = null;
        });

      connectInFlightRef.current = p;
      return p;
    };

    const forceReconnect = (reason) => {
      const es = window.__eventSource;
      if (reason === "manual_click" && es && es.readyState === EventSource.OPEN) {
        console.log("SSE: manual_click ignored — already open");
        return;
      }
      if (connectInFlightRef.current) {
        console.log(`SSE: forceReconnect (${reason}) ignored — connect already in flight`);
        return;
      }
      console.log(`🔄 SSE: forceReconnect (${reason})`);
      reconnectAttemptsRef.current = 0;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
      }
      sseConnectingRef.current = false;
      connectSSE();
    };

    forceReconnectRef.current = forceReconnect;

    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (connectInFlightRef.current) return;
      const es = window.__eventSource;
      if (!es || es.readyState !== EventSource.OPEN) {
        forceReconnect("visibility_visible");
      }
    };

    const onFocus = () => {
      if (connectInFlightRef.current) return;
      const es = window.__eventSource;
      if (!es || es.readyState !== EventSource.OPEN) {
        forceReconnect("window_focus");
      }
    };

    const onOnline = () => {
      if (connectInFlightRef.current) return;
      forceReconnect("network_online");
    };

    const onOffline = () => {
      sseStatusRef.current = "offline";
      setSseStatus("offline");
    };

    const healthInterval = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      if (connectInFlightRef.current) return;
      const es = window.__eventSource;
      if (!es || es.readyState === EventSource.CLOSED) {
        forceReconnect("health_check_closed");
      }
    }, 30000);

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    connectSSE();

    return () => {
      sseConnectingRef.current = false;
      connectInFlightRef.current = null;
      forceReconnectRef.current = null;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      clearInterval(healthInterval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      if (window.__eventSource) {
        try {
          window.__eventSource.onopen = null;
          window.__eventSource.onmessage = null;
          window.__eventSource.onerror = null;
          window.__eventSource.close();
        } catch {
          /* ignore */
        }
        window.__eventSource = null;
        window.__sseCount = Math.max(0, (window.__sseCount || 1) - 1);
      }
    };
  }, [loginToken, dispatch, loggedInUser?.role]);

  useEffect(() => {
    if (!loginToken) return;
    if (connectInFlightRef.current) return;
    const es = window.__eventSource;
    if (!es || es.readyState !== EventSource.OPEN) {
      if (!sseConnectingRef.current) {
        if (forceReconnectRef.current) {
          forceReconnectRef.current("route_change");
        }
      }
    }
  }, [location.pathname, loginToken]);

  useEffect(() => {
    if (prevSseStatusRef.current === "offline" && sseStatus === "open") {
      toast.success("Live updates restored");
    }
    prevSseStatusRef.current = sseStatus;
  }, [sseStatus]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileOpen &&
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target) &&
        profileButtonRef.current &&
        !profileButtonRef.current.contains(event.target)
      ) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [profileOpen]);

  const handleProfileLogout = () => {
    setProfileOpen(false);
    localStorage.setItem("myProcess", "logout");
    dispatch(logout());
    showSwal("Logged Out", "", 200, () => navigate("/loginPage"));
  };

  const handleProfileSettings = () => {
    setProfileOpen(false);
    const rolePath = window.location.pathname.split("/")[1];
    navigate(`/${rolePath}/profile`);
  };

  const toggleNotifications = () => {
    if (!notificationOpen && notificationButtonRef.current) {
      const rect = notificationButtonRef.current.getBoundingClientRect();
      setNotificationPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
    setNotificationOpen(!notificationOpen);
  };

  /* Both of these used to dispatch to redux ONLY — nothing reached the server,
     so the badge came back in full on every reload. Update locally first so the
     bell reacts instantly, then persist; a failed write is logged rather than
     rolled back, because the next fetch re-seeds from the server anyway. */
  const markAsRead = (notificationId) => {
    dispatch(markNotificationRead(notificationId));
    if (!loginToken || !notificationId) return;
    markNotificationReadApi(loginToken, loggedInUser?.role, notificationId).catch(
      (err) => console.error("Could not persist notification read state:", err)
    );
  };

  const markAllAsRead = () => {
    dispatch(markAllNotificationsRead());
    if (!loginToken) return;
    markAllNotificationsReadApi(loginToken, loggedInUser?.role).catch((err) =>
      console.error("Could not persist mark-all-read:", err)
    );
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }

    const target = notificationTarget(notification);

    if (target.kind === "copilot") {
      // The alert asked the operator to open the copilot and request the stale
      // list; the tile now does exactly that instead of dead-ending on
      // /newQuotePage/undefined.
      window.dispatchEvent(
        new CustomEvent("fk:copilot-ask", { detail: { prompt: target.prompt } })
      );
      setNotificationOpen(false);
      return;
    }

    if (target.kind === "notifications") {
      navigate(`/${handleRole(loggedInUser?.role)}/notifications`);
      setNotificationOpen(false);
      return;
    }

    const config = getConversationConfig(notification.entityType);
    navigate(`/${handleRole(loggedInUser?.role)}/newQuotePage/${notification.quoteId}`, {
      state: {
        threadId: notification.threadId,
        entityType: notification.entityType,
        defaultOpen: config.defaultOpen,
        activeConversationTab: config.conversationTab,
        scrollToQuoteConversation: true,
        fromNotification: true,
      },
    });

    setNotificationOpen(false);
  };

  const handleViewAllNotifications = () => {
    setNotificationOpen(false);
    navigate(`/${handleRole(loggedInUser?.role)}/notifications`);
  };

  return (
    <>
      <header
        className="fk-app-header flex h-[60px] min-h-[60px] shrink-0 flex-row items-center justify-between border-b border-slate-200/85 bg-white/70 px-6 text-slate-900 shadow-[0_8px_24px_rgba(15,23,42,0.04)] backdrop-blur-xl backdrop-saturate-150 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.267_0.037_268/0.38)] dark:text-slate-100 dark:shadow-none dark:backdrop-blur-none dark:backdrop-saturate-100 lg:px-8 sticky top-0 z-50"
      >
        <div className="flex items-center gap-3 min-w-0">
          {!mySideBarOpen && (
            <button
              type="button"
              onClick={toggleSidebar}
              aria-label="Open sidebar menu"
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200/70 bg-white/55 shadow-sm transition hover:bg-white/75 hover:shadow-md dark:border-[oklch(0.3_0.02_264/0.7)] dark:bg-[oklch(0.225_0.026_264/0.4)] flex-shrink-0"
            >
              <FontAwesomeIcon icon={faEllipsisVertical} className="text-gray-600 dark:text-gray-300" />
            </button>
          )}
          <div className="min-w-0">
            <h1 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate leading-tight">
              {activePage || "Dashboard"}
            </h1>
            {location.pathname.split("/").filter(Boolean).length > 2 && <BreadcrumbNav />}
          </div>
        </div>

        <div className="flex items-center space-x-4 ml-auto">
          {location.pathname.split("/").includes("complete-quote") && (
            <div className="absolute top-0 right-20 -my-5 w-[min(400px,92vw)]">
              <StageChecklist pT={true} quoteId={id} />
            </div>
          )}

          <Tooltip content="Guided tour for this page" position="bottom">
            <button
              type="button"
              onClick={handleStartPageTour}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition-all hover:border-blue-200 hover:text-blue-600 hover:shadow dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-400 dark:hover:border-blue-500/40 dark:hover:text-blue-400"
              aria-label="Start guided tour for this page"
            >
              <FontAwesomeIcon icon={faQuestionCircle} className="text-[17px]" />
            </button>
          </Tooltip>

          {/* Dark Mode Toggle */}
          <Tooltip content={isDarkMode ? "Switch to light mode" : "Switch to dark mode"} position="bottom">
            <button
              onClick={toggleDarkMode}
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition-all hover:text-slate-700 hover:shadow dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-400 dark:hover:text-slate-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isDarkMode ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                  />
                )}
              </svg>
            </button>
          </Tooltip>

          <div className="relative flex items-center">
            <button
              type="button"
              ref={notificationButtonRef}
              data-tour="sidebar-notifications"
              className="relative flex min-h-9 min-w-9 cursor-pointer items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm transition-all hover:shadow dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)]"
              onClick={toggleNotifications}
              aria-label={
                unreadCount > 0
                  ? `Notifications, ${unreadCount} unread`
                  : "Notifications, no unread items"
              }
            >
              <FontAwesomeIcon
                icon={faBell}
                className="text-[15px] text-slate-500 dark:text-slate-400"
              />
              {unreadCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold animate-pulse"
                  style={{ backgroundColor: '#EF4444' }}
                >
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>

            {notificationOpen && createPortal(
              <div
                ref={notificationPanelRef}
                className="w-[min(24rem,92vw)] max-w-[92vw] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.225_0.026_264)]"
                style={{
                  position: 'fixed',
                  top: `${notificationPosition.top}px`,
                  right: `${notificationPosition.right}px`,
                  zIndex: 99999,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2.5 dark:border-[oklch(0.3_0.02_264)]">
                  <FontAwesomeIcon icon={faBell} className="text-[13px] text-blue-600 dark:text-blue-300" />
                  <span className="text-[13px] font-semibold text-gray-900 dark:text-gray-100">Updates</span>
                  {(realTimeNotifications.length > 0 || unreadCount > 0) && (
                    <span className="text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {realTimeNotifications.length}
                      {unreadCount > 0 && (
                        <span className="ml-1 text-blue-600 dark:text-blue-400">· {unreadCount} new</span>
                      )}
                    </span>
                  )}
                  <div className="flex-1" />
                  <button
                    type="button"
                    aria-pressed={soundMuted}
                    aria-label={
                      soundMuted
                        ? "Unmute notification sounds"
                        : "Mute notification sounds"
                    }
                    title={soundMuted ? "Unmute sounds" : "Mute sounds"}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const next = !soundMuted;
                      setNotificationSoundMuted(next);
                      setSoundMuted(next);
                    }}
                    className="inline-flex min-h-9 min-w-9 items-center justify-center rounded text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-[oklch(0.26_0.028_264)] dark:hover:text-gray-200"
                  >
                    <FontAwesomeIcon icon={soundMuted ? faVolumeMute : faVolumeHigh} className="text-[12px]" />
                  </button>
                  {unreadCount > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        markAllAsRead();
                      }}
                      className="whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium text-gray-500 transition hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-[oklch(0.26_0.028_264)] dark:hover:text-gray-100"
                    >
                      Mark read
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleViewAllNotifications();
                    }}
                    className="inline-flex items-center gap-0.5 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium text-blue-600 transition hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-500/10"
                  >
                    View all
                    <svg className="h-2.5 w-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                <div
                  className={`overflow-y-auto notification-scroll ${isDarkMode ? 'dark' : ''}`}
                  style={{ maxHeight: '420px', minHeight: '200px' }}
                >
                  {realTimeNotifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
                      <AnimatedLoader variant="bellripple" size={110} label="You're all caught up" />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">New updates will show up here instantly.</p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-100 dark:divide-[oklch(0.3_0.02_264)]">
                      {realTimeNotifications.slice(0, 10).map((notification) => {
                        const cfg = entityConfig[notification.entityType] || entityConfig.customer;
                        const unread = !notification.read;
                        return (
                          <li key={notification.id}>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleNotificationClick(notification);
                              }}
                              className={`group relative flex min-h-9 w-full items-start gap-2.5 px-3 py-2.5 text-left transition ${
                                unread
                                  ? "bg-blue-50/40 hover:bg-blue-50/70 before:absolute before:inset-y-2 before:left-0 before:w-[3px] before:rounded-r-full before:bg-blue-500 dark:bg-blue-500/10 dark:hover:bg-blue-500/15"
                                  : "hover:bg-gray-50 dark:hover:bg-[oklch(0.26_0.028_264/0.6)]"
                              }`}
                            >
                              <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${cfg.tile}`}>
                                <FontAwesomeIcon icon={cfg.icon} className="text-[12px]" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="mb-0.5 flex items-center gap-1.5 whitespace-nowrap">
                                  <span className={`shrink-0 rounded px-1.5 py-px text-[10px] font-medium ${cfg.pill}`}>
                                    {capitalizeFirst(notification.entityType)}
                                  </span>
                                  {notification.quoteId && (
                                    <span className="min-w-0 truncate font-mono text-[10.5px] text-gray-400 dark:text-gray-500">
                                      #{notification.quoteId}
                                    </span>
                                  )}
                                  {unread && (
                                    <span className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-blue-500 px-1.5 py-px text-[9.5px] font-semibold uppercase tracking-wide text-white">
                                      <span className="h-1 w-1 animate-pulse rounded-full bg-white" />
                                      Live
                                    </span>
                                  )}
                                </div>
                                <p className="line-clamp-2 text-[12.5px] leading-snug text-gray-800 dark:text-gray-200">
                                  {notification.displayText}
                                </p>
                                <div className="mt-1 flex items-center gap-2.5 text-[10.5px] text-gray-500 dark:text-gray-400">
                                  <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap">
                                    <FontAwesomeIcon icon={faClock} className="text-[9px]" />
                                    {notification.timestamp}
                                  </span>
                                  {notification.entityName && (
                                    <span className="inline-flex min-w-0 items-center gap-1">
                                      <FontAwesomeIcon icon={faBuilding} className="text-[9px] shrink-0" />
                                      <span className="truncate">{notification.entityName}</span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>,
              document.body
            )}

            {/* SSE liveness chip — light, unobtrusive. Drops a transient toast below the chip
                (header sits at the top of the viewport so popping upward would clip). */}
            <div className="relative ml-2 inline-flex shrink-0">
              <AnimatePresence mode="wait">
                {transientStatusMsg && (
                  <motion.div
                    key={transientStatusMsg}
                    role="status"
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.96 }}
                    transition={{ type: "spring", stiffness: 460, damping: 32, mass: 0.65 }}
                    className="pointer-events-none absolute top-full right-0 z-[60] mt-2 w-max min-w-0 max-w-[min(280px,calc(100vw-1.25rem))] origin-top-right"
                  >
                    <SseTransientToastBubble
                      sseStatus={sseStatus}
                      message={transientStatusMsg}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
              <Tooltip
                content={
                  sseStatus === "open"
                    ? "Live updates active"
                    : sseStatus === "connecting"
                      ? "Reconnecting…"
                      : "Live updates disconnected — click to retry"
                }
                position="bottom-end"
                wrapperClassName="inline-flex"
              >
                <button
                  type="button"
                  onClick={handleStatusClick}
                  aria-label={`Live updates status: ${sseStatus}`}
                  className={
                    "inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10.5px] font-medium leading-none transition focus:outline-none " +
                    (sseStatus === "open"
                      ? "cursor-default text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-900/20"
                      : sseStatus === "connecting"
                        ? "cursor-default text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-900/20"
                        : "cursor-pointer text-rose-700 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-900/20")
                  }
                >
                  <span
                    className={
                      sseStatus === "open"
                        ? "h-1.5 w-1.5 rounded-full bg-emerald-500"
                        : sseStatus === "connecting"
                          ? "fk-live-ping h-1.5 w-1.5 rounded-full bg-amber-500"
                          : "fk-live-ping h-1.5 w-1.5 rounded-full bg-rose-500"
                    }
                    aria-hidden
                  />
                  <span>
                    {sseStatus === "open"
                      ? "Live"
                      : sseStatus === "connecting"
                        ? "Reconnecting"
                        : "Offline · Retry"}
                  </span>
                </button>
              </Tooltip>
            </div>
          </div>

          {/* Profile dropdown */}
          <div className="relative">
            <button
              ref={profileButtonRef}
              onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center gap-2 h-10 rounded-2xl border border-gray-200/70 bg-white/55 px-2.5 shadow-sm transition hover:bg-white/75 hover:shadow-md dark:border-[oklch(0.3_0.02_264/0.7)] dark:bg-[oklch(0.225_0.026_264/0.4)]"
            >
              <div className="inline-flex h-7 w-7 items-center justify-center rounded-xl bg-gradient-to-br from-fk-accent to-fk-accent2 text-xs font-bold text-white">
                {userInitial}
              </div>
              {loggedInUser?.name && (
                <span className="hidden sm:block text-sm font-medium text-gray-700 dark:text-gray-200 max-w-[100px] truncate">
                  {loggedInUser.name}
                </span>
              )}
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`text-[10px] text-gray-400 transition-transform duration-200 ${profileOpen ? "rotate-180" : ""}`}
              />
            </button>

            <AnimatePresence>
            {profileOpen && (
              <motion.div
                ref={profileMenuRef}
                variants={popIn}
                initial="hidden"
                animate="show"
                exit="exit"
                className="absolute right-0 top-full z-[200] mt-2 w-56 origin-top-right overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl ring-1 ring-slate-900/5 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:ring-white/10"
                style={{ fontFamily: 'Inter, Manrope, Poppins, sans-serif' }}
              >
                {/* User info */}
                <div className="px-4 py-3 border-b border-gray-100 dark:border-[oklch(0.33_0.022_264/0.5)]">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {loggedInUser?.name || "Account"}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {loggedInUser?.email || ""}
                  </p>
                  {loggedInUser?.role && (
                    <span className="inline-block mt-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-fk-accent/10 text-fk-accent">
                      {loggedInUser.role}
                    </span>
                  )}
                </div>
                {/* Menu items */}
                <div className="py-1">
                  <button
                    onClick={handleProfileSettings}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[oklch(0.3_0.024_264/0.5)] transition-colors"
                  >
                    <FontAwesomeIcon icon={faGear} className="text-gray-400 dark:text-gray-500 w-4" />
                    Settings
                  </button>
                  <button
                    onClick={handleProfileLogout}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                  >
                    <FontAwesomeIcon icon={faRightFromBracket} className="w-4" />
                    Log out
                  </button>
                </div>
              </motion.div>
            )}
            </AnimatePresence>
          </div>
        </div>
      </header>

      {notificationOpen && (
        <div
          className="fixed inset-0"
          style={{
            zIndex: 90,
            pointerEvents: 'auto'
          }}
          onClick={(e) => {
            // Don't close if clicking inside the notification panel
            if (notificationOpen && notificationPanelRef.current && notificationPanelRef.current.contains(e.target)) {
              return;
            }
            // Only close if clicking directly on the backdrop
            if (e.target === e.currentTarget) {
              setNotificationOpen(false);
            }
          }}
        />
      )}
    </>
  );
};

export default Header;
